import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory, useRouteMatch } from "react-router-dom";
import invariant from "tiny-invariant";
import { t } from "@/i18n-js/instance";
import { ModalManager } from "@/react/components/Layout/ModalManagerProvider";
import {
  EDITOR_SELECTION_MODAL_ID,
  EditorSelectionModal,
} from "@/react/components/SettingsApp/Emails/components/Builder/BuilderFields/EditorSelectionModal";
import { VisualBuilderPreviewContent } from "@/react/components/shared/Emails/EmailPreview/PreviewContent/VisualBuilderPreviewContent";
import {
  REPLACE_CONFIRMATION_MODAL_ID,
  ReplaceConfirmationModal,
} from "@/react/components/shared/Emails/Templates/ReplaceConfirmationModal";
import { MarketingEmailTemplatePathProvider } from "@/react/components/shared/Emails/Templates/TemplatePathContext";
import {
  TEMPLATES_MODAL_ID,
  TemplatesModal,
} from "@/react/components/shared/Emails/Templates/TemplatesModal";
import { useTemplatesExists } from "@/react/components/shared/Emails/Templates/hooks/useTemplatesExists";
import { useIsVisualEmailBuilderEnabled } from "@/react/components/shared/Emails/VisualEmailBuilder/useIsVisualEmailBuilderEnabled";
import { useSendTestEmail } from "@circle-react/components/SettingsApp/Emails/hooks/useSendTestEmail";
import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";
import { BroadcastTipTapReadonlyView } from "@circle-react-shared/Emails/EmailPreview/BroadcastTipTapReadonlyView";
import { Button } from "@circle-react-shared/uikit/Button";
import { SkeletonLoader } from "@circle-react-shared/uikit/SkeletonLoader";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useActions, useCurrentAction } from "../../../ActionRule";
import { useResourceStatus } from "../../../WorkflowForm/useResourceStatus";
import { useBroadcastContentPath } from "./useBroadcastContentPath";
import { useEditorUrls } from "./useEditorUrls";
import { useUpsertBroadcast } from "./useUpsertBroadcast";

const localeNamespace = "settings.workflows.form";

interface EmailContentFieldProps {
  lastUpdatedAt?: string;
}
export const EmailContentField = ({
  lastUpdatedAt,
}: EmailContentFieldProps) => {
  const { editorUrls, setEditorUrlsForBroadcast } = useEditorUrls();
  const { watch, setValue } = useFormContext();
  const { paramsPath } = useCurrentAction();
  const history = useHistory();
  const { handleTestEmail, isSending } = useSendTestEmail();
  const { doesTemplatesExist } = useTemplatesExists();

  const formattedLastUpdatedAt = lastUpdatedAt
    ? `${formatDateString(lastUpdatedAt, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} ${t("workflows_v2.action_selector.email.at")} ${formatDateString(
        lastUpdatedAt,
        {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        },
      )}`
    : undefined;

  const emailContent = watch(`${paramsPath}.email_content`);
  const visualEmailBuilderContent = watch(
    `${paramsPath}.visual_email_builder_content`,
  );
  const existingBroadcastId = watch(`${paramsPath}.broadcast_id`);

  const { onUpsert } = useUpsertBroadcast(
    existingBroadcastId ? Number(existingBroadcastId) : null,
  );

  const { doesActionhasServerId, saveAction } = useActions();
  const { action, path } = useCurrentAction();
  const { value } = useResourceStatus(path);
  const { getPathToContentEditor } = useBroadcastContentPath();
  const hasEmailContent = !!emailContent || !!visualEmailBuilderContent;
  const isVisualEmailBuilderEnabled = useIsVisualEmailBuilderEnabled();
  const { isExact } = useRouteMatch();

  useEffect(() => {
    const createBroadcast = async () => {
      const isDuplicatedWorkflowSendEmailAction =
        doesActionhasServerId(action) && emailContent && !existingBroadcastId;

      if (isDuplicatedWorkflowSendEmailAction) {
        const data = await onUpsert();
        const broadcastId = data?.id;
        invariant(
          broadcastId,
          "Broadcast ID is required to start writing email",
        );
        const currentAction = value;
        setValue(`${paramsPath}.broadcast_id`, broadcastId);
        await saveAction({ path, action: currentAction, skipOpenNext: true });
      }
    };

    void createBroadcast();
  }, []);

  const handleNavigationWhenVisualEmailBuilderEnabled = ({
    hasEmailContent,
    broadcastId,
    isVisualEmailBuilderContent,
  }: {
    hasEmailContent: boolean;
    broadcastId: number;
    isVisualEmailBuilderContent: boolean;
  }) => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
    if (hasEmailContent) {
      history.push(
        getPathToContentEditor({
          broadcastId,
          isVisualEmailBuilderContent,
        }),
      );
    } else if (doesTemplatesExist) {
      void ModalManager.show(TEMPLATES_MODAL_ID);
    } else {
      void ModalManager.show(EDITOR_SELECTION_MODAL_ID);
    }
  };

  const handleNavigationWhenVisualEmailBuilderDisabled = ({
    hasEmailContent,
    broadcastId,
    isVisualEmailBuilderContent,
  }: {
    hasEmailContent: boolean;
    broadcastId: number;
    isVisualEmailBuilderContent: boolean;
  }) => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible
    if (doesTemplatesExist && !hasEmailContent) {
      void ModalManager.show(TEMPLATES_MODAL_ID);
    } else {
      history.push(
        getPathToContentEditor({
          broadcastId,
          isVisualEmailBuilderContent,
        }),
      );
    }
  };

  const handleStartWriting = async () => {
    const data = await onUpsert();
    const broadcastId = data?.id;
    const isVisualEmailBuilderContent = !!visualEmailBuilderContent;
    invariant(broadcastId, "Broadcast ID is required to start writing email");
    setValue(`${paramsPath}.broadcast_id`, broadcastId);

    setEditorUrlsForBroadcast(broadcastId.toString());
    isVisualEmailBuilderEnabled
      ? handleNavigationWhenVisualEmailBuilderEnabled({
          hasEmailContent,
          broadcastId,
          isVisualEmailBuilderContent,
        })
      : handleNavigationWhenVisualEmailBuilderDisabled({
          hasEmailContent,
          broadcastId,
          isVisualEmailBuilderContent,
        });
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-between">
        <div className="flex flex-col gap-y-1">
          <Typography.LabelSm weight="semibold" color="text-very-dark">
            {t([localeNamespace, "email_content"])}
          </Typography.LabelSm>
          <Typography.LabelXs color="text-default">
            {hasEmailContent && formattedLastUpdatedAt
              ? t(["workflows_v2.action_selector.email", "last_updated_at"], {
                  timestamp: formattedLastUpdatedAt,
                })
              : t([localeNamespace, "email_content_description"])}
          </Typography.LabelXs>
        </div>
        <div className="flex gap-2">
          {hasEmailContent && (
            <Button
              variant="secondary-light"
              type="button"
              onClick={handleTestEmail}
              disabled={isSending}
            >
              {t("workflows_v2.action_selector.email.send_a_test")}
            </Button>
          )}
          <Button
            variant="secondary-light"
            type="button"
            onClick={handleStartWriting}
            disabled={isSending}
          >
            {hasEmailContent
              ? t("edit")
              : t([localeNamespace, "start_writing"])}
          </Button>
        </div>
      </div>
      <div className="bg-secondary max-h-60 overflow-y-auto rounded-lg px-6 py-5">
        <div className="bg-primary h-[25rem] w-full max-w-[24rem] rounded-lg p-6 shadow">
          {hasEmailContent && isExact ? (
            emailContent ? (
              <BroadcastTipTapReadonlyView content={emailContent} />
            ) : (
              <VisualBuilderPreviewContent />
            )
          ) : (
            <SkeletonLoader variant="email-preview" />
          )}
        </div>
      </div>
      <MarketingEmailTemplatePathProvider paths={editorUrls}>
        <EditorSelectionModal id={EDITOR_SELECTION_MODAL_ID} />
        <TemplatesModal id={TEMPLATES_MODAL_ID} />
        <ReplaceConfirmationModal id={REPLACE_CONFIRMATION_MODAL_ID} />
      </MarketingEmailTemplatePathProvider>
    </div>
  );
};
